import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPOSSales,
  getPOSSaleById,
  updatePOSDeliveryStatus,
} from "../../apis/pos";
import { POSSale } from "../../interfaces/POS";
import {
  Pagination,
  PaginationProps,
  Tag,
  Button,
  Modal,
  message,
  Dropdown,
} from "antd";
import { format } from "date-fns";
import { PrinterOutlined, EditOutlined } from "@ant-design/icons";
import { useNotification } from "../../NotificationContext";
import CommonPagination from "../../components/CommanPagination";

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "paid":
      return "green";
    case "pending":
      return "orange";
    case "cancelled":
    case "rejected":
      return "red";
    case "dispatched":
      return "blue";
    case "received":
      return "purple";
    case "partially_paid":
      return "blue";
    default:
      return "default";
  }
};

const getStatusLabel = (status: string) => {
  if (!status) return "-";
  return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

interface POSSaleResponse extends POSSale {
  patient?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  visitId?: number;
  visit_id?: number;
  saleNumber?: string;
  saleDate?: string;
  totalAmount?: string;
  taxableValue?: string;
  gstAmount?: string;
  discountAmount?: string;
  taxAmount?: string;
  netAmount?: string;
  itemsCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

const POSList: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [meta, setMeta] = useState<any>();
  const [posSales, setPosSales] = useState<POSSaleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState<POSSaleResponse | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [updatingDeliverySaleId, setUpdatingDeliverySaleId] = useState<number | null>(null);

  useEffect(() => {
    fetchPOSSales({ page: 1, per_page: 10 });
  }, []);

  const fetchPOSSales = (params = {}) => {
    setLoading(true);
    getPOSSales({ per_page: 10, ...params })
      .then((res: any) => {
        console.log("POS Sales Response:", res);
        // Handle different response structures
        const salesData = res.data?.data || res.data || [];
        const metaData = res.meta || res.data?.meta || {};
        setPosSales(Array.isArray(salesData) ? salesData : []);
        setMeta(metaData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching POS sales:", err);
        showNotification(
          "error",
          "Error",
          "Failed to fetch POS sales",
          "topRight"
        );
        setLoading(false);
      });
  };

  const handleViewSale = (saleId: number) => {
    getPOSSaleById(saleId)
      .then((res: any) => {
        // Handle different response structures
        const saleData = res.data?.data || res.data || res;
        setSelectedSale(saleData);
        setIsViewModalOpen(true);
      })
      .catch((err) => {
        console.error("Error fetching sale details:", err);
        showNotification(
          "error",
          "Error",
          "Failed to fetch sale details",
          "topRight"
        );
      });
  };


  const calculateTotalItems = (items: any[]) => {
    return items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return `₹${numAmount.toFixed(2)}`;
  };

  const getTaxAmount = (sale: any) => {
    const explicitTax =
      sale.taxAmount ??
      sale.tax_amount ??
      sale.tax ??
      null;

    if (explicitTax !== null && explicitTax !== undefined && explicitTax !== "") {
      return Number(explicitTax) || 0;
    }

    // Fallback when backend doesn't expose tax separately:
    // net = total - discount + tax  => tax = net - total + discount
    const total = Number(sale.totalAmount ?? sale.total_amount ?? 0) || 0;
    const discount = Number(sale.discountAmount ?? sale.discount_amount ?? 0) || 0;
    const net = Number(sale.netAmount ?? sale.net_amount ?? sale.total ?? 0) || 0;
    return Math.max(0, net - total + discount);
  };

  const getTaxableValue = (sale: any) => {
    const explicitTaxable =
      sale.taxableValue ??
      sale.taxable_value ??
      null;
    if (explicitTaxable !== null && explicitTaxable !== undefined && explicitTaxable !== "") {
      return Number(explicitTaxable) || 0;
    }
    const total = Number(sale.totalAmount ?? sale.total_amount ?? 0) || 0;
    const discount = Number(sale.discountAmount ?? sale.discount_amount ?? 0) || 0;
    return Math.max(0, total - discount);
  };

  const getDeliveryMenuItems = (sale: POSSaleResponse) => {
    const isIPD = Boolean(sale.visitId ?? (sale as any).visit_id);
    const rawDelivery =
      sale.deliveryStatus ??
      (sale as any).delivery_status ??
      "pending";
    const delivery = typeof rawDelivery === "string"
      ? rawDelivery.toLowerCase()
      : "pending";

    const items: any[] = [];

    if (isIPD) {
      if (delivery === "pending") {
        items.push({
          key: "delivery_dispatched",
          label: "Dispatched",
        });
      }
      return { deliveryChildren: items };
    }

    if (delivery === "pending") {
      items.push(
        { key: "delivery_completed", label: "Completed" },
        { key: "delivery_dispatched", label: "Dispatched" },
        { key: "delivery_received", label: "Received" },
        { key: "delivery_rejected", label: "Rejected" }
      );
    } else if (delivery === "dispatched") {
      items.push({
        key: "delivery_received",
        label: "Received",
      });
    } else if (delivery === "completed") {
      items.push(
        { key: "delivery_dispatched", label: "Dispatched" },
        { key: "delivery_received", label: "Received" },
        { key: "delivery_rejected", label: "Rejected" }
      );
    }

    return { deliveryChildren: items };
  };

  const handleChangeDeliveryStatus = (
    saleId: number,
    newStatus: "pending" | "dispatched" | "received" | "completed" | "rejected"
  ) => {
    setUpdatingDeliverySaleId(saleId);
    updatePOSDeliveryStatus(saleId, newStatus)
      .then(() => {
        message.success("Delivery status updated successfully");
        setPosSales((prev) =>
          prev.map((sale) =>
            sale.id === saleId
              ? {
                  ...sale,
                  deliveryStatus: newStatus,
                }
              : sale
          )
        );
      })
      .catch((err) => {
        console.error("Error updating delivery status:", err);
        message.error("Failed to update delivery status");
      })
      .finally(() => {
        setUpdatingDeliverySaleId(null);
      });
  };


  // const onPaginationChange: PaginationProps["onChange"] = (page, pageSize) => {
  //   fetchPOSSales({ page, per_page: pageSize });
  // };
  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchPOSSales({ page, per_page: pageSize || 10 });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            POS Sales List
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            List of all pharmacy point of sale transactions.
          </p>
        </div>

        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate("/pharmacy/pos")}
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            New Sale
          </button>
        </div>
      </div>

      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Sale Number
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Items
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Taxable Value
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      GST Amount
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Total
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Delivery Status
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : posSales.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    posSales.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                          <div className="flex flex-col">
                            <div className="flex gap-2 items-center">
                              <span>{sale.saleNumber || `#${sale.id}`}</span>
                              {sale.visitId ?? sale.visit_id ? (
                                <Tag color="blue">IPD</Tag>
                              ) : null}
                            </div>
                            <span className="text-gray-500 text-md">
                              {sale.patient
                                ? `${sale.patient.firstName || ""} ${sale.patient.lastName || ""}`.trim() || "N/A"
                                : "Walk-in Customer"}
                            </span>
                            <span className="text-xs text-gray-400">
                              {sale.saleDate
                                ? format(new Date(sale.saleDate), "dd MMM yyyy")
                                : sale.createdAt
                                  ? format(new Date(sale.createdAt), "dd MMM yyyy")
                                  : "-"}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-4 text-sm text-gray-700">
                          {/* {calculateTotalItems(sale.items || [])} item(s) */}
                          {sale.itemsCount ?? 0} item(s)
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {formatCurrency(getTaxableValue(sale))}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {formatCurrency(getTaxAmount(sale))}
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(
                            sale.netAmount ??
                            (sale as any).net_amount ??
                            (parseFloat(sale.totalAmount ?? "0") -
                              (sale.discountAmount ? parseFloat(sale.discountAmount) : 0) +
                              getTaxAmount(sale))
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          <Tag color={getStatusColor(sale.status || "")}>
                            {getStatusLabel(sale.status || "")}
                          </Tag>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          <Tag color={getStatusColor(sale.deliveryStatus || "")}>
                            {getStatusLabel(sale.deliveryStatus || "")}
                          </Tag>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          <div className="relative px-3 py-4 text-sm font-medium text-left whitespace-nowrap">
                            {sale.status === "cancelled" ? (
                              <Button disabled>Actions</Button>
                            ) : (
                              (() => {
                                const { deliveryChildren } =
                                  getDeliveryMenuItems(sale);

                                const baseItems: any[] = [
                                  {
                                    key: "view",
                                    label: "View",
                                  },
                                  {
                                    key: "edit",
                                    label: "Edit",
                                  },
                                ];

                                if (deliveryChildren && deliveryChildren.length > 0) {
                                  baseItems.push(
                                    { type: "divider" as const },
                                    {
                                      key: "delivery",
                                      label: "Delivery Status",
                                      children: deliveryChildren,
                                    }
                                  );
                                }

                                return (
                                  <Dropdown
                                    menu={{
                                      items: baseItems,
                                      onClick: ({ key }) => {
                                        if (key === "view") {
                                          handleViewSale(sale.id!);
                                        } else if (key === "edit") {
                                          navigate(`/pharmacy/pos/edit/${sale.id}`);
                                        } else if (key.startsWith("delivery_")) {
                                          const statusKey = key.replace(
                                            "delivery_",
                                            ""
                                          ) as
                                            | "pending"
                                            | "dispatched"
                                            | "received"
                                            | "completed"
                                            | "rejected";
                                          handleChangeDeliveryStatus(sale.id!, statusKey);
                                        }
                                      },
                                    }}
                                    placement="bottomLeft"
                                  >
                                    <Button loading={updatingDeliverySaleId === sale.id}>
                                      Actions
                                    </Button>
                                  </Dropdown>
                                );
                              })()
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* <div className="flex justify-end pt-5 pb-4">
              <Pagination
                current={meta?.current_page || 1}
                pageSize={meta?.per_page || 10}
                total={meta?.total || 0}
                onChange={onPaginationChange}
                showSizeChanger
              />
            </div> */}
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      {/* View Sale Modal */}
      <Modal
        title="Sale Details"
        open={isViewModalOpen}
        onCancel={() => {
          setIsViewModalOpen(false);
          setSelectedSale(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsViewModalOpen(false);
            setSelectedSale(null);
          }}>
            Close
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => {
              // Print functionality can be added here
              window.print();
            }}
          >
            Print Receipt
          </Button>,
        ]}
        width={800}
      >
        {selectedSale && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">Sale Number</p>
                <p className="text-sm text-gray-900">
                  {(selectedSale as any).sale_number ?? selectedSale.saleNumber ?? `#${selectedSale.id}`}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Date</p>
                <p className="text-sm text-gray-900">
                  {((selectedSale as any).sale_date ?? selectedSale.saleDate ?? selectedSale.createdAt)
                    ? format(new Date((selectedSale as any).sale_date ?? selectedSale.saleDate ?? selectedSale.createdAt!), "dd MMM yyyy, hh:mm a")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Patient</p>
                <p className="text-sm text-gray-900">
                  {selectedSale.patient
                    ? `${(selectedSale.patient as any).first_name ?? (selectedSale.patient as any).firstName ?? ""} ${(selectedSale.patient as any).last_name ?? (selectedSale.patient as any).lastName ?? ""}`.trim() || "N/A"
                    : "Walk-in Customer"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Doctor</p>
                <p className="text-sm text-gray-900">
                  {((selectedSale as any).doctor)
                    ? `${((selectedSale as any).doctor).first_name ?? ((selectedSale as any).doctor).firstName ?? ""} ${((selectedSale as any).doctor).last_name ?? ((selectedSale as any).doctor).lastName ?? ""}`.trim() || "-"
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Status</p>
                <Tag color={getStatusColor(selectedSale.status || "")}>
                  {getStatusLabel(selectedSale.status || "")}
                </Tag>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">Items</p>
              <div className="overflow-hidden rounded-lg border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Item
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Price
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Discount
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Tax %
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Tax Amount
                      </th>
                      <th className="px-4 py-2 text-xs font-medium text-left text-gray-700">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedSale.items?.map((item: any, index: number) => {
                      const pharmacyItem = item.pharmacy_item || item.pharmacyItem;
                      const batch = item.batch;
                      const name = pharmacyItem?.name ?? item.name;
                      const sku = pharmacyItem?.sku ?? item.sku;
                      const batchNumber = batch?.batch_number ?? batch?.batchNumber;
                      const unitPrice = Number(item.selling_price ?? item.sellingPrice ?? item.mrp ?? item.unitPrice ?? 0);
                      const qty = Number(item.quantity ?? 1);
                      const itemDiscount = Number(item.discount_amount ?? item.discountAmount ?? item.discount ?? 0);
                      const grossAmount = unitPrice * qty;
                      const taxableAfterDiscount = Math.max(0, grossAmount - itemDiscount);
                      const taxPct = Number(item.tax_percentage ?? item.taxPercentage ?? batch?.tax_percentage ?? batch?.taxPercentage ?? batch?.grn_item?.tax_percentage ?? 0);
                      let itemTaxAmount = Number(item.tax_amount ?? item.taxAmount ?? item.gst_amount ?? 0);
                      if (!itemTaxAmount && taxPct > 0) {
                        itemTaxAmount = taxableAfterDiscount - taxableAfterDiscount / (1 + taxPct / 100);
                      }
                      const netAmount = item.net_amount ?? item.netAmount ?? item.total ?? taxableAfterDiscount + itemTaxAmount;
                      return (
                        <tr key={item.id ?? index}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <div>
                              <p className="font-medium">{name ?? "-"}</p>
                              {sku && (
                                <p className="text-xs text-gray-500">SKU: {sku}</p>
                              )}
                              {batchNumber && (
                                <p className="text-xs text-gray-500">
                                  Batch: {batchNumber}
                                  {batch?.expiry_date && (
                                    <span className="ml-1">(Exp: {format(new Date(batch.expiry_date), "MMM yyyy")})</span>
                                  )}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {formatCurrency(itemDiscount)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {taxPct > 0 ? `${taxPct}%` : "-"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {formatCurrency(itemTaxAmount)}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">
                            {formatCurrency(netAmount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex justify-between mb-1 text-sm">
                <span className="text-gray-700">Total Amount:</span>
                <span className="text-gray-900">{formatCurrency((selectedSale as any).total_amount ?? selectedSale.totalAmount ?? selectedSale.subtotal ?? selectedSale.total ?? 0)}</span>
              </div>
              <div className="flex justify-between mb-1 text-sm">
                <span className="text-gray-700">Discount:</span>
                <span className="text-gray-900">{formatCurrency((selectedSale as any).discount_amount ?? selectedSale.discountAmount ?? selectedSale.discount ?? 0)}</span>
              </div>
              <div className="flex justify-between mb-1 text-sm">
                <span className="text-gray-700">Tax:</span>
                <span className="text-gray-900">{formatCurrency((selectedSale as any).tax_amount ?? selectedSale.taxAmount ?? selectedSale.tax ?? 0)}</span>
              </div>
              <div className="flex justify-between pt-2 mt-2 text-lg font-semibold border-t">
                <span className="text-gray-900">Net Amount:</span>
                <span className="text-gray-900">{formatCurrency((selectedSale as any).net_amount ?? selectedSale.netAmount ?? selectedSale.total ?? 0)}</span>
              </div>
            </div>

            {selectedSale.payments && selectedSale.payments.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-700">Payments</p>
                <div className="space-y-2">
                  {selectedSale.payments.map((payment, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {payment.paymentMode.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </p>
                        {payment.transactionId && (
                          <p className="text-xs text-gray-500">
                            Txn ID: {payment.transactionId}
                          </p>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(payment.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedSale.notes && (
              <div>
                <p className="text-sm font-semibold text-gray-700">Notes</p>
                <p className="text-sm text-gray-900">{selectedSale.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default POSList;

